import React, { useEffect, useState } from "react";
import { Plus, Trash2, Copy, Loader2, Ticket, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import AdminShell from "./AdminShell";

const EMPTY = { code: "", type: "percent", value: 10, description: "", active: true, expires_at: "" };

export default function AdminVouchersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vouchers");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load vouchers"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function create() {
    if (!form.code.trim()) {
      toast.error("Code required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/vouchers", {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value) || 0,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
      });
      toast.success(`Voucher ${form.code.toUpperCase()} created`);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
    } finally {
      setCreating(false);
    }
  }

  async function toggle(v) {
    try {
      await api.patch(`/admin/vouchers/${v.id}`, { active: !v.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  }

  async function remove(v) {
    if (!window.confirm(`Delete voucher ${v.code}?`)) return;
    try {
      await api.delete(`/admin/vouchers/${v.id}`);
      toast.success("Voucher deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  }

  function copyCode(code) {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`${code} copied`);
    } catch {
      /* ignore */
    }
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-oj-ink">Vouchers</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Promo codes buyers can apply on any offer at checkout.
          </p>
        </div>
      </div>

      {/* Create form */}
      <section className="rounded-2xl border border-zinc-200 bg-white p-5 mb-6">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-zinc-500 mb-3">Create new voucher</h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_1fr_140px_auto] gap-3">
          <Field label="Code">
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className={inputCls}
              placeholder="WELCOME10"
              data-testid="voucher-code"
            />
          </Field>
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className={inputCls}
              data-testid="voucher-type"
            >
              <option value="percent">Percent %</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </Field>
          <Field label="Value">
            <input
              type="number"
              step="0.01"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className={inputCls}
              data-testid="voucher-value"
            />
          </Field>
          <Field label="Description (shown to buyer)">
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={inputCls}
              placeholder="10% off for first-timers"
            />
          </Field>
          <Field label="Expires (optional)">
            <input
              type="date"
              value={form.expires_at || ""}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className={inputCls}
            />
          </Field>
          <div className="flex items-end">
            <button
              type="button"
              onClick={create}
              disabled={creating}
              data-testid="voucher-create-btn"
              className="inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60"
            >
              {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />} Create
            </button>
          </div>
        </div>
      </section>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="admin-vouchers-table">
        {items.length === 0 && !loading ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No vouchers yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Uses</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} data-testid={`voucher-row-${v.code}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 font-mono font-semibold px-2 py-0.5 rounded">
                        <Ticket className="size-3" /> {v.code}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyCode(v.code)}
                        className="p-1 text-zinc-400 hover:text-oj-ink"
                        title="Copy"
                      >
                        <Copy className="size-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-oj-ink">
                    {v.type === "percent" ? `${v.value}%` : `${v.value} CHF`}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 max-w-xs truncate">{v.description || "—"}</td>
                  <td className="px-4 py-3 text-zinc-500">
                    {v.uses || 0}{v.usage_limit ? ` / ${v.usage_limit}` : ""}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {v.expires_at ? new Date(v.expires_at).toLocaleDateString() : "No expiry"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggle(v)}
                      data-testid={`voucher-toggle-${v.code}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        v.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"
                      }`}
                    >
                      {v.active ? <><Check className="size-3" /> Active</> : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => remove(v)}
                      data-testid={`voucher-delete-${v.code}`}
                      className="p-1.5 rounded hover:bg-red-50"
                    >
                      <Trash2 className="size-4 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}

const inputCls = "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
